/**
 * PURPOSE: Gestión administrativa de un lado del partido en elegibles Rugby (FTR-0015).
 * RESPONSIBILITIES: Estado (configuración mínima del envío de delegados, plazo, bolsa de cambios, regla, bloqueo
 *   derivado del estado actual, versión vigente), edición administrativa de la lista (nunca consume cambios; motivo
 *   obligatorio si el plazo venció o el partido está en vivo; misma clave de idempotencia en los reintentos del mismo
 *   pedido), extensión/desbloqueo con motivo, cambios extra con motivo e historial append-only (revisiones con diff,
 *   cambios extra, plazos). El servidor revalida todo.
 * COLLABORATORS: rugbyLineupsService.js, rugbyLineups.utils.js, RugbyLineupDialogs.jsx, CompanyRugbyLineupsTab.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/RugbyLineupSidePanel.jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  confirmRugbySideAsAdmin,
  getRugbySideHistory,
  getRugbySideState,
  grantRugbyExtraChanges,
  retireRugbyInstitutionExtension,
  saveRugbyInstitutionExtension,
} from "../../services/rugbyLineupsService.js";
import { RugbyDeadlineDialog, RugbyGrantDialog } from "./RugbyLineupDialogs.jsx";
import { StatusNote } from "./RugbyLineupConfigPanels.jsx";
import {
  LOCK_REASON_LABELS,
  OFFICIAL_STATE_LABELS,
  RULE_WARNING_LABELS,
  describeRugbyDeadline,
  describeSubmissionConfiguration,
  entriesFromRows,
  getRugbyErrorMessage,
  newIdempotencyKey,
  rosterRowsFromState,
  summarizeRoster,
} from "./rugbyLineups.utils.js";

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("es-PE", { timeZone: "America/Lima" }) : "—");

const RosterEditor = ({ state, saving, onSubmit }) => {
  const [rows, setRows] = useState(() => rosterRowsFromState(state));
  const [reason, setReason] = useState("");
  const pendingKey = useRef(null);
  useEffect(() => {
    setRows(rosterRowsFromState(state));
    pendingKey.current = null;
  }, [state]);
  const summary = useMemo(() => summarizeRoster(rows), [rows]);
  const update = (idacreditation, patch) => {
    pendingKey.current = null;
    setRows((current) => current.map((row) => (row.idacreditation === idacreditation ? { ...row, ...patch } : row)));
  };
  const reasonRequired = Boolean(state.lock?.reasonRequired);
  const blocked = summary.eligibles === 0 || summary.duplicateJerseys.length > 0 || summary.missingJersey.length > 0 || (reasonRequired && !reason.trim());

  const submit = (event) => {
    event.preventDefault();
    if (blocked) return;
    if (!window.confirm("Se registrará una versión administrativa (no consume cambios de la institución). ¿Confirmar?")) return;
    // La misma clave se reutiliza si el mismo pedido se reintenta tras un error de red.
    if (!pendingKey.current) pendingKey.current = newIdempotencyKey();
    onSubmit({ entries: entriesFromRows(rows), expectedVersion: state.version, idempotencyKey: pendingKey.current, ...(reason.trim() ? { reason: reason.trim() } : {}) });
  };

  return (
    <form onSubmit={submit} className="mt-4">
      <p className="text-sm text-slate-600">
        Elegibles {summary.eligibles} · Titulares {summary.starters} · Regla {state.rule.starterLimit}/{state.rule.eligibleMax}
        {state.rule.source === "FALLBACK_UNCONFIRMED" ? " (sin confirmar: vista previa en advertencia)" : ""}
      </p>
      {summary.duplicateJerseys.length > 0 && <p className="text-sm text-rose-700">Camisetas repetidas: {summary.duplicateJerseys.join(", ")}</p>}
      {summary.missingJersey.length > 0 && <p className="text-sm text-rose-700">Hay elegibles sin número de camiseta.</p>}
      <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Elegible</th>
              <th className="px-3 py-2">Jugador</th>
              <th className="px-3 py-2">Rol</th>
              <th className="px-3 py-2">Camiseta</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.idacreditation} className="border-t border-slate-100">
                <td className="px-3 py-1.5">
                  <input type="checkbox" checked={row.selected} aria-label={`Elegible ${row.name}`} onChange={(event) => update(row.idacreditation, { selected: event.target.checked })} />
                </td>
                <td className="px-3 py-1.5 text-slate-800">{row.name}</td>
                <td className="px-3 py-1.5">
                  <select value={row.role} disabled={!row.selected} onChange={(event) => update(row.idacreditation, { role: event.target.value })} className="rounded border border-slate-300 px-1 py-1 text-xs disabled:opacity-40" aria-label={`Rol ${row.name}`}>
                    <option value="TITULAR">Titular</option>
                    <option value="SUPLENTE">Suplente</option>
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <input type="text" inputMode="numeric" maxLength={3} value={row.jersey} disabled={!row.selected} onChange={(event) => update(row.idacreditation, { jersey: event.target.value.replace(/\D/g, "") })} className="w-16 rounded border border-slate-300 px-1 py-1 text-xs disabled:opacity-40" aria-label={`Camiseta ${row.name}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className="mt-3 block text-xs font-semibold text-slate-600">
        Motivo{reasonRequired ? " * (plazo vencido o partido iniciado)" : " (opcional)"}
        <input type="text" maxLength={255} value={reason} onChange={(event) => setReason(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal" />
      </label>
      <button type="submit" disabled={saving || blocked || !state.lock?.canConfirm} className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300">
        {saving ? "Guardando..." : "Confirmar versión administrativa"}
      </button>
    </form>
  );
};

const History = ({ history }) => (
  <div className="mt-4 space-y-3 text-sm">
    {history.revisions.length === 0 && <p className="text-slate-500">Sin versiones confirmadas.</p>}
    {[...history.revisions].reverse().map((revision) => (
      <div key={revision.id} className="rounded-lg border border-slate-200 p-3">
        <p className="font-semibold text-slate-800">
          Versión {revision.revisionNo} · {revision.actorType === "ADMIN" ? "Administración" : "Delegado"}
          {revision.actorLabel ? ` (${revision.actorLabel})` : ""} · {formatDateTime(revision.createdAt)}
        </p>
        <p className="text-xs text-slate-500">
          {revision.consumedChange ? "Consumió un cambio" : "Sin consumo"}
          {revision.exceededAllowance ? " · por encima de lo permitido" : ""}
          {revision.afterDeadline ? " · después del plazo" : ""}
          {revision.snapshotNo ? ` · alineación oficial ${revision.snapshotNo}` : " · pendiente del rival"}
          {revision.reason ? ` · Motivo: ${revision.reason}` : ""}
        </p>
        <p className="mt-1 text-xs text-slate-600">
          +{revision.diff.added?.length || 0} · −{revision.diff.removed?.length || 0} · cambios {revision.diff.changed?.length || 0} · {revision.entries.length} elegibles
        </p>
        {revision.warnings.length > 0 && (
          <p className="mt-1 text-xs text-amber-700">Advertencias: {revision.warnings.map((item) => item.message || RULE_WARNING_LABELS[item.code] || item.code).join(" · ")}</p>
        )}
      </div>
    ))}
    {history.grants.length > 0 && (
      <div className="rounded-lg border border-slate-200 p-3">
        <p className="font-semibold text-slate-800">Cambios extra</p>
        <ul className="mt-1 text-xs text-slate-600">
          {history.grants.map((grant) => (
            <li key={grant.id}>+{grant.granted} · {grant.reason} · {formatDateTime(grant.createdAt)}</li>
          ))}
        </ul>
      </div>
    )}
    {history.policies.length > 0 && (
      <div className="rounded-lg border border-slate-200 p-3">
        <p className="font-semibold text-slate-800">Plazos del partido y extensiones</p>
        <ul className="mt-1 text-xs text-slate-600">
          {history.policies.map((policy) => (
            <li key={policy.id}>
              {policy.scopeType === "MATCH_INSTITUTION" ? "Extensión" : "Plazo del partido"} · {policy.closesAtLima?.replace("T", " ")} (Lima)
              {policy.reason ? ` · ${policy.reason}` : ""} · {policy.active ? "vigente" : `retirado${policy.retireReason ? ` (${policy.retireReason})` : ""}`}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const RugbyLineupSidePanel = ({ match, side, onClose, onChanged }) => {
  const [tab, setTab] = useState("state");
  const [state, setState] = useState(null);
  const [history, setHistory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const idinstitution = side.institution.id;

  const load = useCallback(async () => {
    try {
      const [nextState, nextHistory] = await Promise.all([getRugbySideState(match.id, idinstitution), getRugbySideHistory(match.id, idinstitution)]);
      setState(nextState);
      setHistory(nextHistory);
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo cargar el lado del partido."));
    }
  }, [match.id, idinstitution]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (work, success) => {
    setSaving(true);
    try {
      await work();
      toast.success(success);
      setDialog(null);
      await load();
      onChanged();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error));
      if (error?.response?.data?.code === "VERSION_CONFLICT") await load();
    } finally {
      setSaving(false);
    }
  };

  const deadline = describeRugbyDeadline(state?.deadline);
  const configuration = describeSubmissionConfiguration(state?.configuration);
  const official = OFFICIAL_STATE_LABELS[state?.officialState] || OFFICIAL_STATE_LABELS.NOT_SUBMITTED;
  const extension = side.extension;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onClose}>
      <div
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !dialog) onClose();
        }}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rugby-side-panel-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="rugby-side-panel-title" className="text-xl font-bold text-slate-800">{side.institution.name}</h3>
            <p className="text-sm text-slate-500">Partido {match.id} · {match.date || "sin fecha"} {match.time || ""}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50">
            Cerrar
          </button>
        </div>

        {!state ? (
          <p className="mt-6 text-sm text-slate-500">Cargando...</p>
        ) : (
          <>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <StatusNote tone={configuration.tone}>{configuration.text}</StatusNote>
              </div>
              <StatusNote tone={deadline.tone}>{deadline.text}</StatusNote>
              <StatusNote tone={official.tone}>{official.text} · versión {state.version}</StatusNote>
              <StatusNote tone="slate">
                Cambios: {state.allowance.remainingChanges} disponibles de {state.allowance.allowedChanges + state.allowance.extraChanges}
                {state.allowance.excessChanges ? ` · ${state.allowance.excessChanges} en exceso` : ""}
              </StatusNote>
              <StatusNote tone={state.lock.status === "OPEN" ? "emerald" : "amber"}>
                {state.lock.status === "OPEN" ? "Abierto" : state.lock.reasons.map((reason) => LOCK_REASON_LABELS[reason] || reason).join(" · ")}
              </StatusNote>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => setDialog("extension")} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                {extension ? "Cambiar extensión" : "Extender / desbloquear"}
              </button>
              {extension && (
                <button
                  type="button"
                  onClick={() => {
                    const reason = window.prompt("Se retirará la extensión de esta institución. Motivo (opcional):", "");
                    if (reason !== null) run(() => retireRugbyInstitutionExtension(match.id, idinstitution, reason.trim()), "Extensión retirada.");
                  }}
                  className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                >
                  Retirar extensión
                </button>
              )}
              <button type="button" onClick={() => setDialog("grant")} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                Otorgar cambios extra
              </button>
            </div>

            <div className="mt-5 flex gap-1 rounded-lg border border-slate-200 p-1" role="tablist" aria-label="Lado del partido">
              {[["state", "Versión y edición"], ["history", "Historial"]].map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold ${tab === id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {label}
                </button>
              ))}
            </div>
            {tab === "state" ? (
              <>
                {state.draft?.stale === false && <p className="mt-3 text-xs text-slate-500">La institución tiene un borrador sin confirmar ({state.draft.entries.length} jugadores).</p>}
                <RosterEditor state={state} saving={saving} onSubmit={(payload) => run(() => confirmRugbySideAsAdmin(match.id, idinstitution, payload), "Versión administrativa confirmada.")} />
              </>
            ) : (
              history && <History history={history} />
            )}
          </>
        )}

        {dialog === "extension" && (
          <RugbyDeadlineDialog
            title="Extender o desbloquear el envío"
            subtitle={`${side.institution.name}: nueva fecha límite solo para esta institución (hora de Lima).`}
            initialLima={extension?.closesAtLima || ""}
            reasonRequired
            saving={saving}
            onCancel={() => setDialog(null)}
            onSubmit={(payload) => run(() => saveRugbyInstitutionExtension(match.id, idinstitution, payload), "Extensión guardada.")}
          />
        )}
        {dialog === "grant" && (
          <RugbyGrantDialog
            subtitle={`${side.institution.name}: los cambios extra se suman a los permitidos.`}
            saving={saving}
            onCancel={() => setDialog(null)}
            onSubmit={(payload) => run(() => grantRugbyExtraChanges(match.id, idinstitution, payload), "Cambios extra otorgados.")}
          />
        )}
      </div>
    </div>
  );
};

export default RugbyLineupSidePanel;
